import { User } from '../types';
import { functions } from './firebase';
import { httpsCallable } from 'firebase/functions';

declare const Razorpay: any;

interface PaymentOptions {
    planId: string; // The ONLY source of truth for pricing/credits
    type: 'subscription' | 'addon';
    user: User;
    onSuccess: (paymentId: string) => void;
    onFailure: (error: Error) => void;
    onOrderCreated?: (orderId: string) => void; 
}

const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (typeof Razorpay !== 'undefined') {
            resolve(true);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            resolve(false);
        };
        document.body.appendChild(script);
    });
};

export const initializePayment = async ({ planId, type, user, onSuccess, onFailure, onOrderCreated }: PaymentOptions) => {

    // 1. Get Key (Support both Vite and standard env)
    const keyId = import.meta.env.VITE_RAZORPAY_KEY_ID || process.env.RAZORPAY_KEY_ID;

    if (!keyId) {
        console.error("Client Key Missing: Check your .env file for VITE_RAZORPAY_KEY_ID");
        onFailure(new Error("Configuration Error: Client Razorpay Key ID is missing."));
        return;
    }

    // 2. Ensure SDK is loaded
    try {
        const isLoaded = await loadRazorpayScript();
        if (!isLoaded) {
            throw new Error("Payment gateway failed to load. Please check your internet connection.");
        }
    } catch (error: any) {
        onFailure(error);
        return;
    }

    try {
        // 3. SECURITY: Call Backend to create Order using ONLY Plan ID
        const createOrderFn = httpsCallable(functions, 'createRazorpayOrder');
        
        // We do NOT send price/credits here. The server looks them up from planId.
        const orderResponse = await createOrderFn({ 
            planId: planId 
        });
        
        const orderData = orderResponse.data as any;

        if (!orderData || !orderData.id) {
            throw new Error("Failed to create secure payment order.");
        }

        // 4. Track Order ID for recovery (Safety Net)
        if (onOrderCreated) {
            onOrderCreated(orderData.id);
        }

        const options = {
            key: keyId,
            amount: orderData.amount, // Server decided amount
            currency: orderData.currency,
            name: "Najeweller AI",
            description: `Plan: ${planId.toUpperCase()}`,
            image: "https://api.dicebear.com/8.x/initials/svg?seed=N",
            order_id: orderData.id, // Server-Side Order ID
            
            // Metadata for dashboard tracking
            notes: {
                userId: user.id,
                userEmail: user.email,
                paymentType: type,
                planId: planId // Reference for manual checks
            },

            // 5. SECURITY: Verify Payment on Backend
            handler: async function (response: any) {
                try {
                    const verifyPaymentFn = httpsCallable(functions, 'verifyPayment');
                    
                    // We send planId again for double-verification
                    await verifyPaymentFn({
                        paymentId: response.razorpay_payment_id,
                        orderId: response.razorpay_order_id,
                        signature: response.razorpay_signature,
                        planId: planId 
                    });
                    
                    onSuccess(response.razorpay_payment_id);
                } catch (err) {
                    console.error("Verification failed", err);
                    onFailure(new Error("Payment verification failed. Please use 'Verify Status' in your wallet to check for missing credits."));
                }
            },
            prefill: {
                name: user.name,
                email: user.email,
            },
            theme: {
                color: "#d4af37", // Apple Gold
            },
            modal: {
                ondismiss: function() {
                    onFailure(new Error("Payment cancelled"));
                }
            }
        };

        const rzp = new Razorpay(options);
        
        // Handle explicit failures
        rzp.on('payment.failed', function (response: any) {
            console.error("Payment Failed:", response.error);
            onFailure(new Error(response.error.description || "Payment failed"));
        });

        rzp.open();

    } catch (e: any) {
        console.error("Payment initialization error:", e);
        const msg = e.details || e.message || "Failed to initialize payment transaction.";
        
        if (msg.includes("configuration missing")) {
            onFailure(new Error("Server Error: Razorpay keys are not set in the Backend .env file."));
        } else {
            onFailure(new Error(msg));
        }
    }
};